import React from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { GoalMultiplierCanSpinMarker } from "@/apps/goals/components/GoalMultiplier/GoalMultiplierCanSpinMarker";
import { GoalMultiplierLockedMarker } from "@/apps/goals/components/GoalMultiplier/GoalMultiplierLockedMarker";
import { GoalMultiplierValueMarker } from "@/apps/goals/components/GoalMultiplier/GoalMultiplierValueMarker";
import { useGoalsStore } from "@/apps/goals/store/goalsStore";
import Typography from "@/ui/Typography/Typography";
import { useServiceT } from "@/utils/hooks/useServiceT";
import cls from "./styles.module.scss";

interface GoalProgressBlockProps {
  completedRides: number;
  ridesRequired: number;
  variant: "active" | "completed" | "failed";
}

export const GoalProgressBlock = ({
  completedRides,
  ridesRequired,
  variant,
}: GoalProgressBlockProps) => {
  const { t } = useTranslation();
  const serviceT = useServiceT();
  const multiplier = useGoalsStore((state) => state.initData?.multiplier);
  const rewardAmount = useGoalsStore(
    (state) => state.initData?.program.goal_price ?? 0
  );
  const progress =
    ridesRequired > 0
      ? Math.min((completedRides / ridesRequired) * 100, 100)
      : 0;
  const showMultiplierLocked =
    variant === "active" &&
    Boolean(multiplier) &&
    multiplier?.value == null &&
    progress < 50;
  const showMultiplierCanSpin =
    variant === "active" &&
    Boolean(multiplier) &&
    multiplier?.value == null &&
    progress >= 50;
  const showMultiplierValue = variant === "active" && multiplier?.value != null;
  const showMultiplierMarker =
    showMultiplierLocked || showMultiplierCanSpin || showMultiplierValue;

  return (
    <div
      className={[
        cls.progressBlock,
        showMultiplierMarker ? cls.withMultiplierMarker : "",
      ].join(" ")}
    >
      <div className={cls.progressMeta}>
        <Typography
          component="span"
          size="md"
          weight="semibold"
          family="Onest"
          color="var(--palette-white)"
        >
          {t("goals.common.progress", {
            completed: completedRides,
            required: ridesRequired,
          })}
        </Typography>
        <Typography component="span" size="sm" weight="bold" family="Onest">
          {serviceT("goals.common.rides", { count: ridesRequired })}
        </Typography>
      </div>
      <div className={cls.progressTrack}>
        <div
          className={cn(
            cls.progressFill,
            cls[variant],
            showMultiplierValue && cls.multiplierFill
          )}
          style={{ width: `${progress}%` }}
        />
        {showMultiplierLocked && (
          <GoalMultiplierLockedMarker
            progress={progress}
            rewardAmount={rewardAmount}
          />
        )}
        {showMultiplierCanSpin && <GoalMultiplierCanSpinMarker />}
        {showMultiplierValue && (
          <GoalMultiplierValueMarker value={multiplier.value} />
        )}
      </div>
    </div>
  );
};
